import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { of, Observable } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, tap } from 'rxjs/operators';
import { CommunicationsApiService } from './communications-api.service';
import { ConfiguredChannel, ChannelType, DistributionList, Template, ChannelUIds } from './communications.model';
import { GUID_EMPTY } from '@app/core/constants';
import { TranslateService } from '@ngx-translate/core';
import { v4 } from 'uuid';
import { FilterGroupType } from 'src/app/modules/core/store/models/filters.model';
import Environment from '@env';
import * as commActions from './communications.actions';
import * as commSelectors from './communications.selectors';
import * as fromRouting from 'src/app/modules/core/store/routing';
import * as fromAuth from 'src/app/modules/authentication/store/auth/auth.selectors';
import * as fromdata from 'src/app/modules/management/store/data';
import { DataApiService } from '../data/data-api.service';
import { Logger } from 'src/app/modules/core/services/logger/logger.model';
import { LoggerService } from 'src/app/modules/core/services/logger/logger.service';
import { Location } from '@angular/common';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Injectable()
export class CommunicationEffects {
  private logger: Logger;

  constructor(
    private actions$: Actions,
    private commApiService: CommunicationsApiService,
    private dataApiService: DataApiService,
    private store$: Store<any>,
    private notify: SystemNotificationsService,
    private translateService: TranslateService,
    private loggerService: LoggerService,
    private location: Location
  ) {
    this.logger = this.loggerService.getLogger('CommunicationEffects');
  }

  //
  // Channels
  //
  getOrganizationChannelsFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getOrganizationChannelsFromApi),
      withLatestFrom(this.store$.select(commSelectors.selectOrganizationChannelQueryParams)),
      switchMap(([action, queryParams]) => {
        return this.commApiService.getOrganizationChannels(queryParams).pipe(
          map(results => {
            return commActions.getOrganizationChannelsFromApiComplete({ data: results });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  getConfiguredChannelsFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getConfiguredChannelsFromApi),
      switchMap(action => {
        const { queryParams } = action;
        return this.commApiService.getConfiguredChannels(queryParams).pipe(
          map(results => {
            return commActions.getConfiguredChannelsFromApiComplete({ data: results });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  newTopoEmailChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newTopoEmailChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {},
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editTopoEmailChannel({ channel: newChannel })];
      })
    )
  );

  newSendGridEmailChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newSendGridEmailChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {
            fromEmail: '',
            apiToken: ''
          },
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editSendGridEmailChannel({ channel: newChannel })];
      })
    )
  );

  newEverbridgeChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newEverbridgeChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {
            organizationId: '',
            username: '',
            password: ''
          },
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editEverbridgeChannel({ channel: newChannel })];
      })
    )
  );

  newSlackChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newSlackChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {
            isConnected: false
          },
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editSlackChannel({ channel: newChannel })];
      })
    )
  );

  newTeamsChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newTeamsChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {
            isConnected: false
          },
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editMicrosoftTeamsChannel({ channel: newChannel })];
      })
    )
  );

  newTwilioChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newTwilioChannel),
      switchMap(action => {
        const { channel } = action;

        const newChannel: ConfiguredChannel = {
          uId: GUID_EMPTY,
          channelUId: channel.channelUId,
          name: channel.name,
          description: this.translateService.instant(channel.description),
          channelName: channel.name,
          channelDescription: channel.description,
          channelType: channel.channelType,
          channelTypeDisplay: this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channel.channelType}`),
          logoImagePath: channel.logoImagePath,
          isActive: true,
          settings: {},
          secureSettings: {},
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null
        };

        return [commActions.editTwilioChannel({ channel: newChannel })];
      })
    )
  );

  saveConfiguredChannelToApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveConfiguredChannelToApi),
      switchMap(action => {
        const { channel } = action;

        return this.commApiService.saveConfiguredChannel(channel).pipe(
          map(updatedChannel => {
            return commActions.saveConfiguredChannelToApiComplete({ channelUId: updatedChannel.channelUId });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  saveAndConnectToSlack$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveAndConnectToSlack),
      switchMap(action => {
        const { channel } = action;

        return this.commApiService.saveConfiguredChannel(channel).pipe(
          switchMap(updatedChannel => {
            return [
              commActions.updateSelectedConfiguredChannel({ channel: updatedChannel }),
              commActions.connectToSlack({ channel: updatedChannel })
            ];
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  connectToSlack$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(commActions.connectToSlack),
        switchMap(action => {
          const { channel } = action;
          const clientId = Environment.slack.client_id;
          const scopes = 'commands,chat:write,chat:write.public,channels:read,groups:read,users:read,users:read.email,im:write';
          const redirectUrl = encodeURIComponent(
            `${window.location.protocol}//${window.location.host}/api/v1/notifications/slack/auth/callback`
          );

          return this.commApiService.createChannelAuthStateToken(channel).pipe(
            map(stateToken => {
              const state = encodeURIComponent(stateToken);
              const url = `https://slack.com/oauth/v2/authorize?client_id=${clientId}&scope=${scopes}&redirect_uri=${redirectUrl}&state=${state}`;
              window.location.href = url;
            }),
            catchError(err => {
              return of(commActions.displayChannelError({ err }));
            })
          );
        })
      ),
    { dispatch: false }
  );

  getSlackChannelsForConfiguredChannelFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getSlackChannelsForConfiguredChannelFromApi),
      switchMap(action => {
        const { channel } = action;

        return this.commApiService.getSlackChannelsForConfiguredChannel(channel).pipe(
          map(channels => {
            return commActions.getSlackChannelsForConfiguredChannelFromApiComplete({ channels });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  disconnectFromSlackWithApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.disconnectFromSlackWithApi),
      switchMap(action => {
        const { channel } = action;

        return this.commApiService.disconnectChannelFromSlack(channel).pipe(
          map(updatedChannel => {
            return commActions.disconnectFromSlackWithApiComplete({ channel: updatedChannel });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  saveConfiguredChannelToApiComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveConfiguredChannelToApiComplete),
      withLatestFrom(this.store$.select(commSelectors.selectConfiguredChannelQueryParams)),
      switchMap(([action, queryParams]) => {
        return [commActions.closeConfiguredChannelEdit(), commActions.getConfiguredChannelsFromApi({ queryParams })];
      })
    )
  );

  closeConfiguredChannelEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.closeConfiguredChannelEdit),
      switchMap(action => {
        // Change URL without actually initiating a router navigation so that we preserve the list state
        this.location.go('/management/communication-center/channels');
        return [commActions.clearSelectedConfiguredChannel()];
      })
    )
  );

  deleteConfiguredChannelApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteConfiguredChannelApi),
      switchMap(action => {
        const { channel } = action;

        return this.commApiService.deleteConfiguredChannel(channel).pipe(
          map(results => {
            return commActions.deleteConfiguredChannelApiComplete();
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  deleteConfiguredChannelApiComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteConfiguredChannelApiComplete),
      withLatestFrom(this.store$.select(commSelectors.selectConfiguredChannelQueryParams)),
      switchMap(([action, queryParams]) => {
        return [commActions.clearChannelToDelete(), commActions.getConfiguredChannelsFromApi({ queryParams })];
      })
    )
  );

  //
  // Distribution Lists
  //
  getDistributionListsFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getDistributionListsFromApi),
      switchMap(action => {
        return this.commApiService.getDistributionLists(action.queryParams).pipe(
          map(results => {
            return commActions.getDistributionListsFromApiComplete({ data: results });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  newDistributionList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newDistributionList),
      switchMap(action => {
        const distributionList: DistributionList = {
          uId: GUID_EMPTY,
          name: '',
          description: '',
          channelType: ChannelType.Email,
          channelUId: null,
          nameFieldUId: null,
          destinationFieldUId: null,
          isActive: true,
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null,
          queryMatchRule: {
            uId: GUID_EMPTY,
            datasetUId: null,
            root: {
              uId: v4(),
              filters: [],
              filterGroups: [],
              groupType: FilterGroupType.And
            }
          }
        };

        return [commActions.newDistributionListComplete({ distributionList })];
      })
    )
  );

  saveDistributionListToApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveDistributionListToApi),
      switchMap(action => {
        const { distributionList } = action;
        const isNew = distributionList.uId === GUID_EMPTY;

        return this.commApiService.saveDistributionList(distributionList).pipe(
          switchMap(distributionList => {
            if (isNew) {
              return [
                commActions.saveDistributionListToApiComplete({ distributionList }),
                fromRouting.go({
                  routeData: { path: ['management', 'communication-center', 'distribution-lists', distributionList.uId] }
                })
              ];
            } else {
              return [
                commActions.cancelEditDistributionListComplete(),
                fromRouting.go({
                  routeData: { path: ['management', 'communication-center', 'distribution-lists'] }
                })
              ];
            }
          }),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  editDistributionList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.editDistributionList),
      switchMap(action => {
        const { distributionList } = action;

        return [
          commActions.editDistributionListComplete({ distributionList }),
          fromRouting.go({
            routeData: { path: ['management', 'communication-center', 'distribution-lists', distributionList.uId] }
          })
        ];
      })
    )
  );

  cancelEditDistributionList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.cancelEditDistributionList),
      switchMap(action => {
        return [
          commActions.cancelEditDistributionListComplete(),
          fromRouting.go({
            routeData: { path: ['management', 'communication-center', 'distribution-lists'] }
          })
        ];
      })
    )
  );

  deleteDistributionListApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteDistributionListApi),
      switchMap(action => {
        const { distributionList } = action;

        return this.commApiService.deleteDistributionList(distributionList).pipe(
          map(results => {
            return commActions.deleteDistributionListApiComplete();
          }),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  deleteDistributionListApiComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteDistributionListApiComplete),
      withLatestFrom(this.store$.select(commSelectors.selectDistributionListsQueryParams)),
      switchMap(([action, queryParams]) => {
        return [commActions.clearDistributionListToDelete(), commActions.getDistributionListsFromApi({ queryParams })];
      })
    )
  );

  getDocumentsWithFilterFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getDocumentsWithFilterFromApi),
      switchMap(action => {
        const { datasetUId, queryParams, filter } = action;
        return this.commApiService.getDocumentsWithFilter(datasetUId, queryParams, filter).pipe(
          map(results => commActions.getDocumentsWithFilterFromApiComplete({ results })),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  saveDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveDocument),
      withLatestFrom(
        this.store$.select(commSelectors.selectDistributionListDataQueryParams),
        this.store$.select(fromAuth.selectUserContext)
      ),
      switchMap(([action, params, userContext]) => {
        const datasetUId = action.dataset.uId;

        const docToSave = fromdata.populateDefaults(action.document, action.dataset.workflow, userContext);

        if (action.isNewDocument) {
          return this.dataApiService.createDocument(datasetUId, docToSave).pipe(
            map(results => commActions.getDocumentsWithFilterFromApi({ datasetUId, queryParams: params, filter: action.filter })),
            catchError(err => of(commActions.displayDistributionListError({ err })))
          );
        } else {
          return this.dataApiService.updateDocument(action.dataset.uId, docToSave).pipe(
            map(results => commActions.getDocumentsWithFilterFromApi({ datasetUId, queryParams: params, filter: action.filter })),
            catchError(err => of(commActions.displayDistributionListError({ err })))
          );
        }
      })
    )
  );

  getDatasetFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getDatasetFromApi),
      switchMap(action => {
        const { datasetUId } = action;
        return this.commApiService.getDataset(datasetUId).pipe(
          map(dataset => commActions.getDatasetFromApiComplete({ dataset })),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  //
  // Templates
  //
  getTemplatesFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getTemplatesFromApi),
      switchMap(action => {
        return this.commApiService.getTemplates(action.queryParams).pipe(
          map(results => {
            return commActions.getTemplatesFromApiComplete({ data: results });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  newTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.newTemplate),
      switchMap(action => {
        const template: Template = {
          uId: GUID_EMPTY,
          name: '',
          description: '',
          channelType: ChannelType.Email,
          datasetUId: null,
          layout: null,
          isActive: true,
          createdOn: new Date(),
          updatedOn: new Date(),
          lastEditedByName: null,
          lastEditedById: null,
          settings: {
            toRecipients: [],
            ccRecipients: [],
            bccRecipients: [],
            toReadOnly: false,
            ccReadOnly: false,
            subject: null,
            sendAsGroup: false,
            sendDocumentAttachments: false
          }
        };

        return [commActions.newTemplateComplete({ template })];
      })
    )
  );

  saveTemplateToApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.saveTemplateToApi),
      withLatestFrom(this.store$.select(commSelectors.selectTemplateQueryParams)),
      switchMap(([action, queryParams]) => {
        const { template, skipNavigation } = action;
        const isNew = template.uId === GUID_EMPTY;

        return this.commApiService.saveTemplate(template).pipe(
          switchMap(updatedTemplate => {
            if (isNew) {
              return [
                commActions.saveTemplateToApiComplete({ template: updatedTemplate }),
                fromRouting.go({
                  routeData: { path: ['management', 'communication-center', 'templates', updatedTemplate.uId] }
                })
              ];
            } else {
              if (!skipNavigation) {
                return [
                  commActions.cancelEditTemplateComplete(),
                  fromRouting.go({
                    routeData: { path: ['management', 'communication-center', 'templates'] }
                  })
                ];
              } else {
                return [commActions.getTemplatesFromApi({ queryParams })];
              }
            }
          }),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  copyTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.copyTemplate),
      withLatestFrom(this.store$.select(commSelectors.selectTemplateQueryParams)),
      switchMap(([action, queryParams]) => {
        const { template } = action;

        return this.commApiService.copyTemplate(template).pipe(
          switchMap(copiedTemplate => {
            return [
              commActions.saveTemplateToApiComplete({ template: copiedTemplate }),
              commActions.clearTemplateToCopy(),
              fromRouting.go({
                routeData: { path: ['management', 'communication-center', 'templates', copiedTemplate.uId] }
              })
            ];
          }),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  editTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.editTemplate),
      switchMap(action => {
        const { template } = action;

        return [
          commActions.editTemplateComplete({ template }),
          fromRouting.go({
            routeData: { path: ['management', 'communication-center', 'templates', template.uId] }
          })
        ];
      })
    )
  );

  cancelEditTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.cancelEditTemplate),
      switchMap(action => {
        return [
          commActions.cancelEditDistributionListComplete(),
          fromRouting.go({
            routeData: { path: ['management', 'communication-center', 'templates'] }
          })
        ];
      })
    )
  );

  deleteTemplateApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteTemplateApi),
      switchMap(action => {
        const { template } = action;

        return this.commApiService.deleteTemplate(template).pipe(
          map(results => {
            return commActions.deleteTemplateApiComplete();
          }),
          catchError(err => of(commActions.displayDistributionListError({ err })))
        );
      })
    )
  );

  deleteTemplateApiComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.deleteTemplateApiComplete),
      withLatestFrom(this.store$.select(commSelectors.selectTemplateQueryParams)),
      switchMap(([action, queryParams]) => {
        return [commActions.clearTemplateToDelete(), commActions.getTemplatesFromApi({ queryParams })];
      })
    )
  );

  // Everbridge Templates
  getEverbridgeTemplatesFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.getEverbridgeTemplatesFromApi),
      switchMap(action => {
        return this.commApiService.getEverbridgeTemplatesForConfiguredChannel(action.channel).pipe(
          map(results => {
            return commActions.getEverbridgeTemplatesFromApiComplete({ templates: results });
          }),
          catchError(err => of(commActions.displayChannelError({ err })))
        );
      })
    )
  );

  displayChannelError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.displayChannelError),
      switchMap(action => {
        this.logger.logError('Channel error: ' + action.err);
        if (action.err && action.err.length > 0) {
          const errorMessage = this.translateService.instant(action.err);
          this.notify.error(errorMessage);
        }
        return [];
      })
    )
  );

  displayDistributionListError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(commActions.displayDistributionListError),
      switchMap(action => {
        this.logger.logError('Distributino List error: ' + action.err);
        if (action.err && action.err.length > 0) {
          const errorMessage = this.translateService.instant(action.err);
          this.notify.error(errorMessage);
        }
        return [];
      })
    )
  );
}
